import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Tabs,
  Tab,
  Container
} from '@mui/material';
import {
  CalendarTodayOutlined as CalendarIcon,
  Computer as WorkstationsIcon,
  MedicalServicesOutlined as ExaminationsIcon,
  Assignment as ScanProtocolsIcon,
  AccountTreeOutlined as StructuredIcon,
  TextFieldsOutlined as FreetextIcon,
  TextSnippetOutlined as SnippetsIcon,
  DescriptionOutlined as LayoutIcon,
  SummarizeOutlined as ReportingIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders  } from '../../utils/authUtils';
import { apiFetch, getApiBaseUrl } from '../../utils/apiConfig';

// Import subcomponents (to be created)
import CalendarSettings from './reportingManagement/CalendarSettings';
import Workstations from './reportingManagement/Workstations';
import Examinations from './reportingManagement/Examinations';
import ScanProtocols from './reportingManagement/ScanProtocols';
import StructuredReporting from './reportingManagement/StructuredReporting';
import FreetextReporting from './reportingManagement/FreetextReporting';
import SnippetsManagement from './reportingManagement/SnippetsManagement';
import ReportLayout from './reportingManagement/ReportLayout';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reporting-tabpanel-${index}`}
      aria-labelledby={`reporting-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const ReportingManagement = ({ initialTab = 0, hideHeader = false }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Sync activeTab with initialTab when it changes
  React.useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Calendar Settings
  const [calendarSettings, setCalendarSettings] = useState({
    working_hours_start: '08:00',
    working_hours_end: '17:00',
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    default_appointment_duration: 30,
    allow_weekend_appointments: false,
    advance_booking_days: 30,
    appointment_reminder_hours: 24
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Load calendar settings
  const loadCalendarSettings = useCallback(async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await apiFetch('/admin/calendar-settings');
      
      if (response.ok) {
        const data = await response.json();
        setCalendarSettings(data);
      }
    } catch (err) {
      console.error('Error loading calendar settings:', err);
    }
  }, []);

  // Save calendar settings
  const saveCalendarSettings = async () => {
    setLoading(true);
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/admin/calendar-settings`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(calendarSettings)
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: t('admin.reportingManagement.calendarSettings.saveSuccess') 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: t('admin.reportingManagement.calendarSettings.saveError') 
        });
      }
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: t('admin.reportingManagement.calendarSettings.saveError') 
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarSettings();
  }, [loadCalendarSettings]);

  // Wenn hideHeader true ist, zeige nur den Content direkt an
  if (hideHeader) {
    return (
      <Box sx={{ overflow: 'auto' }}>
        <TabPanel value={activeTab} index={0}>
          <CalendarSettings
            settings={calendarSettings}
            setSettings={setCalendarSettings}
            loading={loading}
            saveSettings={saveCalendarSettings}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Workstations />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Examinations />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <ScanProtocols />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <StructuredReporting />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <FreetextReporting />
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          <SnippetsManagement />
        </TabPanel>

        <TabPanel value={activeTab} index={7}>
          <ReportLayout />
        </TabPanel>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReportingIcon color="primary" />
        {t('admin.tabs.reportingManagement')}
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mt: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="reporting management tabs"
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<CalendarIcon />} 
            label={t('admin.reportingManagement.calendarSettings.title')} 
            id="reporting-tab-0"
            aria-controls="reporting-tabpanel-0"
          />
          <Tab 
            icon={<WorkstationsIcon />} 
            label={t('admin.reportingManagement.workstations.title')} 
            id="reporting-tab-1"
            aria-controls="reporting-tabpanel-1"
          />
          <Tab 
            icon={<ExaminationsIcon />} 
            label={t('admin.reportingManagement.examinations.title')} 
            id="reporting-tab-2"
            aria-controls="reporting-tabpanel-2"
          />
          <Tab 
            icon={<ScanProtocolsIcon />} 
            label={t('admin.reportingManagement.scanProtocols.title')} 
            id="reporting-tab-3"
            aria-controls="reporting-tabpanel-3"
          />
          <Tab 
            icon={<StructuredIcon />} 
            label={t('admin.reportingManagement.structuredReporting.title')} 
            id="reporting-tab-4"
            aria-controls="reporting-tabpanel-4"
          />
          <Tab 
            icon={<FreetextIcon />} 
            label={t('admin.reportingManagement.freetextReporting.title')} 
            id="reporting-tab-5"
            aria-controls="reporting-tabpanel-5"
          />
          <Tab 
            icon={<SnippetsIcon />} 
            label={t('admin.reportingManagement.snippets.title')} 
            id="reporting-tab-6"
            aria-controls="reporting-tabpanel-6"
          />
          <Tab 
            icon={<LayoutIcon />} 
            label={t('admin.reportingManagement.reportLayout.title')} 
            id="reporting-tab-7"
            aria-controls="reporting-tabpanel-7"
          />
        </Tabs>

        <Box sx={{ overflow: 'auto' }}>
          <TabPanel value={activeTab} index={0}>
            <CalendarSettings
              settings={calendarSettings}
              setSettings={setCalendarSettings}
              loading={loading}
              saveSettings={saveCalendarSettings}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Workstations />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Examinations />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <ScanProtocols />
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <StructuredReporting />
          </TabPanel>

          <TabPanel value={activeTab} index={5}>
            <FreetextReporting />
          </TabPanel>

          <TabPanel value={activeTab} index={6}>
            <SnippetsManagement />
          </TabPanel>

          <TabPanel value={activeTab} index={7}>
            <ReportLayout />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportingManagement; 